import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDeleteLeft,
  faInbox,
  faPaperclip,
} from '@fortawesome/free-solid-svg-icons';
import { Progress } from 'react-sweet-progress';
import 'react-sweet-progress/lib/style.css';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { useContext } from 'react';
import { NotificationContext } from 'context/NotificationContext';
import { ApiConsumer } from 'api/ApiConsumer';
import { API_ROUTES } from 'routes/api';
import { useAuthentication } from 'hooks/AuthenticationHook';

const MAX_UPLOAD_BYTES = 1024 * 1024 * 1024;
const MAX_UPLOAD_LABEL = '1 GB';

const _validFileExtensions = ['.mp3', '.wav', '.mp4', '.mp4v'];

export default function ScanUploadFile({
  upload_progress,
  icon,
  icon_size,
  setFile,
  message = 'Click or drag file to this area to upload',
}) {
  const [file_name, setFiles] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  let { setNotification } = useContext(NotificationContext);
  let { user } = useAuthentication();

  const handleChange = (e) => {
    const file = e.target.files[0];
    validateAndSetFile(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    validateAndSetFile(file);
  };

  const validateAndSetFile = (file) => {
    if (!file) return;

    // Check file extension
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!_validFileExtensions.includes(fileExt)) {
      setNotification({
        show: true,
        title: 'Invalid File Type',
        message: `Only ${_validFileExtensions.join(', ')} files are allowed`,
        type: 'error',
      });
      return;
    }

    // 1 GB. A six-hour event recording is around 513 MB, which the old 500 MB cap rejected even
    // though the backend handled that exact file: nginx accepts 3 GB bodies, and Django now streams
    // every upload to disk rather than holding it in memory.
    if (file.size > MAX_UPLOAD_BYTES) {
      setNotification({
        show: true,
        title: 'File Too Large',
        message: `File size exceeds the ${MAX_UPLOAD_LABEL} limit`,
        type: 'error',
      });
      return;
    }

    setFiles(file.name);
    setFile(file);
  };

  const handleDelete = () => {
    setFiles('');
    setFile(null);
  };

  return (
    <div className="flex grid grid-cols-1 bg-white justify-center w-full">
      <div className="flex items-center justify-center w-full">
        <label 
          className={`flex flex-col w-full bg-white h-full border-2 border-dashed 
            ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-100 hover:border-gray-300'}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center pt-7">
            <FontAwesomeIcon 
              icon={icon} 
              size={icon_size} 
              className={isDragging ? 'text-blue-500' : ''} 
            />
            <p className="pt-2 text-base tracking-wider group-hover:text-gray-600">
              {isDragging ? 'Drop your file here' : message}
            </p>
          </div>
          <input
            type="file"
            name="file"
            onChange={handleChange}
            className="opacity-0"
            accept={_validFileExtensions.join(',')}
          />
        </label>
      </div>

      {file_name && (
        <div className="flex justify-between w-full p-2">
          <div>
            <p>
              <span>
                <FontAwesomeIcon icon={faPaperclip} className="pr-2" />
              </span>
              {file_name}
            </p>
          </div>
          <div>
            <FontAwesomeIcon 
              icon={faTrashCan} 
              onClick={handleDelete} 
              className="cursor-pointer hover:text-red-500"
            />
          </div>
        </div>
      )}

      {upload_progress > 0 && (
        <div className="mt-2">
          <Progress
            percent={upload_progress}
            status="success"
            theme={{
              success: {
                color: '#2ecc71',
                symbol: upload_progress + '%',
              },
            }}
          />
        </div>
      )}
    </div>
  );
}